// src/components/family/profile/ProfileQuickStats.tsx
// Quick statistics grid for child profile overview

import React from 'react';
import {
  Calendar,
  Target,
  FileText,
  Users,
  Activity,
  Clock,
  TrendingUp,
  Sparkles,
  type LucideIcon,
} from 'lucide-react';
import type { ChildProfileStats } from '@/features/children/hooks/useChildProfile';
import { formatDistanceToNow } from 'date-fns';

interface StatCardProps {
  label: string;
  value: string | number;
  subtext?: string;
  icon: LucideIcon;
  color: 'sage' | 'terracotta' | 'navy' | 'success';
  highlight?: boolean;
}

const colorClasses = {
  sage: {
    bg: 'bg-sage/10',
    text: 'text-sage-dark',
    icon: 'text-sage',
  },
  terracotta: {
    bg: 'bg-terracotta/10',
    text: 'text-terracotta',
    icon: 'text-terracotta',
  },
  navy: {
    bg: 'bg-navy/10',
    text: 'text-navy',
    icon: 'text-navy-light',
  },
  success: {
    bg: 'bg-emerald-50',
    text: 'text-emerald-600',
    icon: 'text-emerald-500',
  },
};

const StatCard: React.FC<StatCardProps> = ({
  label,
  value,
  subtext,
  icon: Icon,
  color,
  highlight = false,
}) => {
  const colors = colorClasses[color];

  return (
    <div
      className={`
        relative p-4 rounded-xl border transition-all duration-200
        ${highlight 
          ? 'bg-gradient-to-br from-sage/5 to-sage/10 border-sage/20 shadow-sm' 
          : 'bg-white border-cream-dark hover:border-sage/30 hover:shadow-sm'
        }
      `}
    >
      {/* Highlight sparkle */}
      {highlight && (
        <Sparkles className="absolute top-3 right-3 w-4 h-4 text-sage/40" />
      )}

      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-lg ${colors.bg} flex items-center justify-center flex-shrink-0`}>
          <Icon className={`w-5 h-5 ${colors.icon}`} />
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-sm text-warm-gray font-medium truncate">{label}</p>
          <p className={`text-2xl font-display font-semibold ${colors.text} mt-0.5`}>
            {value}
          </p>
          {subtext && (
            <p className="text-xs text-warm-gray mt-1">{subtext}</p>
          )}
        </div>
      </div>
    </div>
  );
};

interface ProfileQuickStatsProps {
  stats: ChildProfileStats;
  className?: string;
}

export const ProfileQuickStats: React.FC<ProfileQuickStatsProps> = ({
  stats,
  className = '',
}) => {
  // Format last health log date
  const lastLogText = stats.lastHealthLogDate
    ? formatDistanceToNow(new Date(stats.lastHealthLogDate), { addSuffix: true })
    : 'No logs yet';

  return (
    <div className={className}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-display font-semibold text-navy">
          Quick Stats
        </h3>
        <span className="text-xs text-warm-gray">
          {stats.daysInCare} days of care tracking
        </span>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {/* Upcoming appointments */}
        <StatCard
          label="Upcoming"
          value={stats.upcomingAppointmentsCount}
          subtext={stats.upcomingAppointmentsCount === 1 ? 'appointment' : 'appointments'}
          icon={Calendar}
          color="sage"
        />

        {/* Active goals */}
        <StatCard
          label="Active Goals"
          value={stats.activeGoalsCount}
          subtext={`${stats.completedGoalsCount} completed`}
          icon={Target}
          color="terracotta"
        />

        {/* Documents */}
        <StatCard
          label="Documents"
          value={stats.documentsCount}
          subtext="stored securely"
          icon={FileText}
          color="navy"
        />

        {/* Care team */}
        <StatCard
          label="Care Team"
          value={stats.careTeamMembersCount}
          subtext="providers"
          icon={Users}
          color="sage"
        />
      </div>

      {/* Seizure-free streak (if applicable) */}
      {stats.seizureFreeStreak !== undefined && stats.seizureFreeStreak > 0 && (
        <div className="mt-4">
          <StatCard
            label="Seizure-Free Streak"
            value={`${stats.seizureFreeStreak} days`}
            subtext="Keep up the great progress! 🎉"
            icon={Activity}
            color="success"
            highlight={true}
          />
        </div>
      )}

      {/* Last activity indicator */}
      <div className="mt-4 flex items-center gap-2 text-sm text-warm-gray">
        <Clock className="w-4 h-4" />
        <span>Last health log: {lastLogText}</span>
      </div>
    </div>
  );
};

// Loading skeleton
export const ProfileQuickStatsSkeleton: React.FC = () => (
  <div className="animate-pulse">
    <div className="flex items-center justify-between mb-4">
      <div className="h-6 bg-cream-dark rounded w-28" />
      <div className="h-4 bg-cream-dark rounded w-36" />
    </div>

    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="p-4 bg-white rounded-xl border border-cream-dark">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-cream-dark" />
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-cream-dark rounded w-16" />
              <div className="h-7 bg-cream-dark rounded w-12" />
              <div className="h-3 bg-cream-dark rounded w-20" />
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

export default ProfileQuickStats;
